import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Icon from "components/AppIcon";
// Assuming you use a toast library like 'sonner' or 'react-hot-toast'
import { toast } from "sonner"; 

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const INDICATOR_FIELD_CONFIG = [
  {
    key: "findings",
    label: "Findings / Observations",
    helper: "Document the core observations, evidence trail, and assessment findings for this indicator.",
    placeholder: "Write the findings and observations for this monitoring indicator...",
    variant: "primary",
  },
  {
    key: "remarks",
    label: "Remarks",
    helper: "Add clarifications, follow-ups, and any other relevant notes.",
    placeholder: "Add final remarks for this indicator...",
    variant: "compact",
  },
];

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getIndicatorStatus(values) {
  const hasFindings = Boolean(stripHtml(values?.findings || ""));
  const hasPoints = String(values?.points || "").trim() !== "";
  if (!hasFindings && !hasPoints) return "Not Started";
  if (hasFindings && hasPoints) return "Complete";
  return "In Progress";
}

function statusClass(status) {
  if (status === "Complete") return "bg-success/10 text-success";
  if (status === "In Progress") return "bg-[#16325a]/10 text-[#16325a]";
  return "bg-muted text-muted-foreground";
}

function MonitoringFieldEditor({ label, helper, placeholder, value, onChange, variant, isEditable }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="flex flex-col xl:grid xl:grid-cols-[220px_minmax(0,1fr)]">
        <div className="border-b border-border bg-muted/25 px-4 py-4 xl:border-b-0 xl:border-r">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{helper}</p>
        </div>
        <div className="p-4">
          <div className={`monitoring-editor ${variant === "primary" ? "monitoring-editor--primary" : "monitoring-editor--compact"}`}>
            <ReactQuill
              theme="snow"
              value={value}
              onChange={onChange}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder={placeholder}
              readOnly={!isEditable}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SubIndicatorItem({ sub, indicatorCode, draftValues, onFieldChange, onPointsChange, onPointsBlur, isEditable }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const subKey = `${indicatorCode}.${sub.subCode}`;
  const subStatus = getIndicatorStatus(draftValues[subKey] || {});
  
  const parseScoringCriteria = (description) => {
    if (!description) return [];
    const lines = description.split("\n").filter(line => line.trim());
    return lines.map((line, idx) => {
      const match = line.match(/If.*?Score (\d+)/i);
      const score = match ? parseInt(match[1], 10) : null;
      return { text: line, score, id: idx };
    });
  };
  
  const criteria = parseScoringCriteria(sub.description);

  return (
    <div className="ml-6 mt-4 first:mt-0">
      <div className="rounded-xl border border-border bg-background overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/10 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3 flex-1">
            <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                  {sub.subCode}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusClass(subStatus)}`}>
                  {subStatus}
                </span>
              </div>
              <p className="text-sm font-medium mt-1">{sub.subName}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Max Score</p>
            <p className="text-sm font-semibold">{sub.maximumScore}</p>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-border p-4 space-y-4">
            {criteria.length > 0 && (
              <div className="rounded-lg bg-muted/15 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Scoring Criteria
                </p>
                <ul className="space-y-1.5">
                  {criteria.map(criterion => (
                    <li key={criterion.id} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-[#16325a] shrink-0" />
                      <span>
                        {criterion.text}
                        {criterion.score && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-[#16325a]/10 text-[#16325a]">
                            {criterion.score} pts
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg border border-border">
              <div className="grid gap-3 p-4 lg:grid-cols-[200px_1fr]">
                <div>
                  <p className="text-sm font-medium">Points Awarded</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Enter points (0-{sub.maximumScore})
                  </p>
                </div>
                <div className="max-w-[200px]">
                  <Input
                    type="number"
                    min="0"
                    max={sub.maximumScore}
                    step="0.1"
                    placeholder={`0 - ${sub.maximumScore}`}
                    value={draftValues[subKey]?.points || ""}
                    onChange={(e) => onPointsChange(subKey, sub.maximumScore, e.target.value)}
                    onBlur={() => onPointsBlur(subKey, sub.maximumScore)}
                    disabled={!isEditable}
                  />
                </div>
              </div>
            </div>

            {INDICATOR_FIELD_CONFIG.map((field) => (
              <MonitoringFieldEditor
                key={`${sub.subIndicatorId}-${field.key}`}
                label={field.label}
                helper={field.helper}
                placeholder={field.placeholder}
                value={draftValues[subKey]?.[field.key] || ""}
                onChange={(value) => onFieldChange(subKey, field.key, value)}
                variant={field.variant}
                isEditable={isEditable}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function IndicatorComponent({ 
  indicator, 
  subIndicators, 
  draftValues, 
  onFieldChange, 
  onPointsChange,
  onPointsBlur,
  onSaveIndicator,
  savingIndicator,
  index,
  totalIndicators,
  isEditable = true,
  statusButtons = null
}) {
  const isSaving = savingIndicator === indicator.indicatorId;

  const overallStatus = () => {
    const completed = subIndicators.filter(sub => {
      const subKey = `${indicator.code}.${sub.subCode}`;
      const status = getIndicatorStatus(draftValues[subKey] || {});
      return status === "Complete";
    }).length;
    
    if (completed === 0) return "Not Started";
    if (completed === subIndicators.length) return "Complete";
    return "In Progress";
  };

  const status = overallStatus();
  const completedCount = subIndicators.filter(sub => {
    const subKey = `${indicator.code}.${sub.subCode}`;
    const status = getIndicatorStatus(draftValues[subKey] || {});
    return status === "Complete";
  }).length;

  // UPDATED: Added async/await and Toast feedback
  const handleSaveIndicator = async () => {
    const indicatorScores = {};
    subIndicators.forEach(sub => {
      const subKey = `${indicator.code}.${sub.subCode}`;
      const data = draftValues[subKey] || {};
      indicatorScores[sub.subCode] = {
        indicatorName: sub.subName,
        score: parseFloat(data.points) || 0,
        findings: data.findings || "",
        remarks: data.remarks || ""
      };
    });
    
    try {
      await onSaveIndicator(indicator, indicatorScores);
      toast.success(`Indicator ${indicator.code} saved successfully`);
    } catch (error) {
      toast.error(`Failed to save ${indicator.code}. Please try again.`);
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="p-5 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                {indicator.code}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusClass(status)}`}>
                {status}
              </span>
              <span className="text-xs text-muted-foreground">
                Item {index + 1}/{totalIndicators}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-foreground">{indicator.name}</h3>
            {indicator.description && (
              <p className="text-sm text-muted-foreground mt-2">{indicator.description}</p>
            )}
          </div>
          <div className="flex items-center gap-4">
            {statusButtons}
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Progress</p>
              <p className="text-lg font-semibold">
                {completedCount}/{subIndicators.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {subIndicators.map((sub) => (
          <SubIndicatorItem
            key={sub.subIndicatorId}
            sub={sub}
            indicatorCode={indicator.code}
            draftValues={draftValues}
            onFieldChange={onFieldChange}
            onPointsChange={onPointsChange}
            onPointsBlur={onPointsBlur}
            isEditable={isEditable}
          />
        ))}
      </div>

      {/* Save Button Container - Right Aligned & Slate Styled */}
      <div className="flex items-end justify-end p-4 border-t border-border bg-muted/5">
        {isEditable && (
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={handleSaveIndicator}
            disabled={isSaving}
            className="bg-slate-900 hover:bg-slate-800 text-white transition-colors min-w-[140px]"
          >
            {isSaving ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" className="mr-2 h-4 w-4" />
                Save Indicator
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}